import React, { useState, useCallback, useEffect } from 'react';
import {
	CCard, CCardBody, CCardHeader, CCol, CRow, CButton,
	CAccordion, CAccordionItem, CAccordionHeader, CAccordionBody,
	CFormInput, CFormSelect, CBadge,
} from '@coreui/react';
import { DataGrid } from '@mui/x-data-grid';
import { Link, useLoaderData, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import AxiosInstance from 'src/components/Axios';
import { cilPlus, cilSearch, cilPencil } from '@coreui/icons';
import CIcon from '@coreui/icons-react';
import { useSupportChannels } from 'src/hooks/useSupportChannels';
import { useResponderTeams } from 'src/hooks/useResponderTeams';
import { useSupportCategories } from 'src/hooks/useSupportCategories';
import SupportLettersModal from './SupportLettersModal';
import { STATUS_LETTER_OPTIONS, STATUS_COLORS, STATUS_LABELS, SENDER_TYPE_COLORS, SENDER_TYPE_LABELS, formatDateTime } from 'src/constants/supportLetters';
import { isValidEmailFormat } from 'src/utils/email';
import { formatPhoneInput, isValidPhoneInput, PHONE_PREFIX } from 'src/utils/inputValidation';
import { toast, ToastContainer } from 'react-toastify';

const urlApi = '/support-requests';
const urlSupportLetters = '/support-letters';
const itemsPerPage = 25;
const LIST_CACHE_KEY = 'support_letters_list';

export async function LoaderSupportLetters() {
	return AxiosInstance.get(urlApi, { params: { page: 1, limit: itemsPerPage } });
}

const SupportLetters = () => {
	const loaderData = useLoaderData();
	const navigate = useNavigate();

	const channels = useSupportChannels();
	const teams = useResponderTeams();
	const allCategories = useSupportCategories();

	// Ініціалізація: кеш → лоадер
	const getInitialRows = () => {
		try {
			const cached = JSON.parse(sessionStorage.getItem(LIST_CACHE_KEY));
			if (cached?.rows) return cached.rows;
		} catch { }
		return loaderData?.data?.data ?? [];
	};
	const getInitialTotal = () => {
		try {
			const cached = JSON.parse(sessionStorage.getItem(LIST_CACHE_KEY));
			if (cached?.total != null) return cached.total;
		} catch { }
		return loaderData?.data?.total ?? 0;
	};

	const [rows, setRows] = useState(getInitialRows);
	const [rowCountState, setRowCountState] = useState(getInitialTotal);
	const [showLoader, setShowLoader] = useState(false);
	const [page, setPage] = useState(0);

	const [statusFilter, setStatusFilter] = useState('');
	const [emailFilter, setEmailFilter] = useState('');
	const [phoneFilter, setPhoneFilter] = useState(PHONE_PREFIX);
	const [dateFrom, setDateFrom] = useState('');
	const [dateTo, setDateTo] = useState('');
	const [channelFilter, setChannelFilter] = useState('');
	const [teamFilter, setTeamFilter] = useState([]);
	const [categoryFilter, setCategoryFilter] = useState([]);
	const [filterErrors, setFilterErrors] = useState({});

	const [modalOpen, setModalOpen] = useState(false);
	const [modalRequestId, setModalRequestId] = useState(null);

	const handleBack = useCallback(() => navigate(-1), [navigate]);

	// Зберегти список в кеш після завантаження
	const saveToCache = (data) => {
		try { sessionStorage.setItem(LIST_CACHE_KEY, JSON.stringify(data)); } catch { }
	};

	// Фонове оновлення при маунті (якщо показали з кешу)
	useEffect(() => {
		const cached = sessionStorage.getItem(LIST_CACHE_KEY);
		if (cached) {
			fetchRows(1, true);
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const buildParams = (page = 1) => {
		const params = { page, limit: itemsPerPage };
		if (statusFilter) params.status = statusFilter;
		if (emailFilter.trim()) params.email = emailFilter.trim();
		if (phoneFilter !== PHONE_PREFIX) params.phone = phoneFilter;
		if (channelFilter) params.channel_id = channelFilter;
		if (dateFrom) params.date_from = dateFrom;
		if (dateTo) params.date_to = dateTo;
		if (teamFilter.length) params.responder_team_ids = teamFilter.map(t => t.value);
		if (categoryFilter.length) params.category_ids = categoryFilter.map(c => c.value);
		return params;
	};

	const fetchRows = (page = 1, silent = false) => {
		if (!silent) setShowLoader(true);
		AxiosInstance.get(urlApi, { params: buildParams(page) })
			.then(res => {
				const newRows = res.data.data ?? [];
				const newTotal = res.data.total ?? 0;
				setRows(newRows);
				setRowCountState(newTotal);
				saveToCache({ rows: newRows, total: newTotal });
			})
			.catch(() => { })
			.finally(() => { if (!silent) setShowLoader(false); });
	};

	const handleKeyDown = (e) => {
		if (e.key === 'Enter') handleSearch();
	};

	const handleSearch = () => {
		const newErrors = {};
		if (emailFilter.trim() && !isValidEmailFormat(emailFilter)) newErrors.email = true;
		if (phoneFilter !== PHONE_PREFIX && !isValidPhoneInput(phoneFilter)) newErrors.phone = true;

		if (Object.keys(newErrors).length) {
			setFilterErrors(newErrors);
			const msgs = [newErrors.email && 'Email', newErrors.phone && 'Телефон'].filter(Boolean);
			toast.warning(`Невірний формат: ${msgs.join(', ')}`);
			return;
		}

		setFilterErrors({});
		setPage(0);
		fetchRows(1);
	};

	const handleClear = () => {
		setStatusFilter('');
		setEmailFilter('');
		setPhoneFilter(PHONE_PREFIX);
		setFilterErrors({});
		setDateFrom('');
		setDateTo('');
		setChannelFilter('');
		setTeamFilter([]);
		setCategoryFilter([]);
		setPage(0);
		setShowLoader(true);
		AxiosInstance.get(urlApi, { params: { page: 1, limit: itemsPerPage } })
			.then(res => {
				const newRows = res.data.data ?? [];
				const newTotal = res.data.total ?? 0;
				setRows(newRows);
				setRowCountState(newTotal);
				saveToCache({ rows: newRows, total: newTotal });
			})
			.catch(() => { })
			.finally(() => setShowLoader(false));
	};

	const handlePageChange = (newPage) => {
		setPage(newPage);
		fetchRows(newPage + 1);
	};

	const handleOpenModal = (id) => {
		setModalRequestId(id);
		setModalOpen(true);
	};

	const teamOptions = [{ value: -1, label: 'Без команди' }, ...teams.map(t => ({ value: t.id, label: t.name }))];
	const categoryOptions = [{ value: -1, label: 'Без категорії' }, ...allCategories.map(c => ({ value: c.id, label: c.name }))];

	const columns = [
		{ field: 'id', headerName: 'ID', width: 70 },
		{
			field: 'sender_type', headerName: 'Від', width: 70,
			renderCell: (params) => (
				<CBadge color={SENDER_TYPE_COLORS[params.row.sender_type] || 'secondary'}>
					{SENDER_TYPE_LABELS[params.row.sender_type] || params.row.sender_type || '—'}
				</CBadge>
			),
		},
		{
			field: 'user_email', headerName: 'Email', width: 250,
			renderCell: (params) => (
				<Link to={`${urlSupportLetters}/view/${params.row.id}`} className="table-name-link">
					{params.row.user_email || '—'}
				</Link>
			),
		},
		{
			field: 'session_id', headerName: '№ сесії', width: 70,
			renderCell: (params) => (
				<Link to={`${urlSupportLetters}/session/${params.row.session_id}`} className="table-name-link" title={params.row.session_id ? `Перейти на сесію №${params.row.session_id}` : '—'}>
					{params.row.session_id || '—'}
				</Link>
			),
		},
		{
			field: 'subject', headerName: 'Тема',
			width: 260,
			// flex: 1,
			renderCell: (params) => (
				<Link to={`${urlSupportLetters}/view/${params.row.id}`} className="table-name-link" title={params.row.subject || '—'}>
					{params.row.subject || '—'}
				</Link>
			),
		},
		{
			field: 'status', headerName: 'Статус', width: 160,
			renderCell: (params) => (
				<CBadge color={STATUS_COLORS[params.row.status] || 'secondary'}>
					{STATUS_LABELS[params.row.status] || params.row.status || '—'}
				</CBadge>
			),
		},
		{
			field: 'responders', headerName: 'Команди відповідальних', width: 220, sortable: false,
			renderCell: (params) => {
				const responders = params.row.responders || [];
				if (responders.length === 0) return <span className="text-muted">—</span>;
				return (
					<div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
						{responders.map(r => (
							<CBadge key={r.team_id} color="info">{r.team_name} ({r.team_code})</CBadge>
						))}
					</div>
				);
			},
		},
		{
			field: 'channel', headerName: 'Канал', width: 120,
			renderCell: (params) => params.row.channel?.name || '—',
		},
		{
			field: 'categories', headerName: 'Категорії', width: 200,
			renderCell: (params) => {
				const text = (params.row.categories || []).map(c => c.name).join(', ') || '—';
				return <span title={text}>{text}</span>;
			},
		},
		{
			field: 'sent_at', headerName: 'Дата отримання', width: 140,
			renderCell: (params) => formatDateTime(params.row.sent_at || params.row.created_at),
		},
		{
			field: 'created_at', headerName: 'Дата внесення', width: 140,
			renderCell: (params) => formatDateTime(params.row.created_at),
		},
		{
			field: 'updated_at', headerName: 'Останнє оновлення', width: 145,
			renderCell: (params) => formatDateTime(params.row.updated_at),
		},
		{
			field: 'action', headerName: 'Переглянути', headerAlign: 'center', align: 'center', sortable: false, width: 105,
			renderCell: (params) => (
				<Link to={`${urlSupportLetters}/view/${params.row.id}`} className="custom-view-icon" title="Переглянути запит">
					<CIcon icon={cilSearch} customClassName="nav-icon" height={18} />
				</Link>
			),
		},
		{
			field: 'action_modal', headerName: '👁', headerAlign: 'center', align: 'center', sortable: false, width: 60,
			renderCell: (params) => (
				<button
					className="custom-view-icon"
					title="Швидкий перегляд"
					onClick={() => handleOpenModal(params.row.id)}
					style={{ background: 'none', cursor: 'pointer' }}
				>
					👁
				</button>
			),
		},
		{
			field: 'action_edit', headerName: 'Редагувати', headerAlign: 'center', align: 'center', sortable: false, width: 95,
			renderCell: (params) => (
				<Link to={`${urlSupportLetters}/update/${params.row.id}`} className="custom-edit-icon" title="Редагувати запит">
					<CIcon icon={cilPencil} customClassName="nav-icon" height={18} />
				</Link>
			),
		},
	];

	return (
		<>
			<ToastContainer />
			<SupportLettersModal
				open={modalOpen}
				requestId={modalRequestId}
				onClose={() => setModalOpen(false)}
			/>
			<CRow>
				<CCol xs={12}>
					<CCard className="mb-4">
						<CAccordion flush>
							<CAccordionItem>
								<CAccordionHeader><strong>Фільтр</strong></CAccordionHeader>
								<CAccordionBody>
									<CRow className="my-3 mx-2 g-2">
										<CCol md={2}>
											<CFormSelect
												value={statusFilter}
												onChange={e => setStatusFilter(e.target.value)}
												title="Фільтр по статусу"
											>
												<option value="">Всі статуси</option>
												{STATUS_LETTER_OPTIONS.map(o => (
													<option key={o.value} value={o.value}>{o.label}</option>
												))}
											</CFormSelect>
										</CCol>
										<CCol md={2}>
											<CFormSelect
												value={channelFilter}
												onChange={e => setChannelFilter(e.target.value)}
												title="Фільтр по каналу зв'язку"
											>
												<option value="">Всі канали</option>
												{channels.map(ch => (
													<option key={ch.id} value={ch.id}>{ch.name}</option>
												))}
											</CFormSelect>
										</CCol>
										<CCol md={2}>
											<CFormInput
												placeholder="Email"
												value={emailFilter}
												onChange={e => {
													setEmailFilter(e.target.value);
													if (filterErrors.email) setFilterErrors(p => ({ ...p, email: false }));
												}}
												onKeyDown={handleKeyDown}
												invalid={!!filterErrors.email}
												autoComplete="off"
												title="Пошук по email відправника"
											/>
										</CCol>
										<CCol md={2}>
											<CFormInput
												placeholder="Телефон"
												value={phoneFilter}
												onChange={e => {
													const formatted = formatPhoneInput(e.target.value);
													if (formatted !== null) {
														setPhoneFilter(formatted);
														if (filterErrors.phone) setFilterErrors(p => ({ ...p, phone: false }));
													}
												}}
												onKeyDown={handleKeyDown}
												invalid={!!filterErrors.phone}
												autoComplete="off"
												title="Пошук по телефону відправника"
											/>
										</CCol>
										<CCol md={2}>
											<CFormInput
												type="date"
												value={dateFrom}
												onChange={e => setDateFrom(e.target.value)}
												onKeyDown={handleKeyDown}
												title="Дата від"
											/>
										</CCol>
										<CCol md={2}>
											<CFormInput
												type="date"
												value={dateTo}
												onChange={e => setDateTo(e.target.value)}
												onKeyDown={handleKeyDown}
												title="Дата до"
											/>
										</CCol>
									</CRow>
									<CRow className="mx-2 g-2">
										<CCol md={4}>
											<Select
												isMulti
												options={teamOptions}
												value={teamFilter}
												onChange={setTeamFilter}
												placeholder="Команди..."
												isClearable
												classNamePrefix="react-select"
											/>
										</CCol>
										<CCol md={4}>
											<Select
												isMulti
												options={categoryOptions}
												value={categoryFilter}
												onChange={setCategoryFilter}
												placeholder="Категорії..."
												isClearable
												classNamePrefix="react-select"
											/>
										</CCol>
										<CCol md={4} className="d-flex gap-2 justify-content-end align-items-center">
											<CButton color="primary" onClick={handleSearch} title="Застосувати фільтри">
												Пошук
											</CButton>
											<CButton color="secondary" variant="outline" onClick={handleClear} title="Скинути всі фільтри">
												Очистити
											</CButton>
										</CCol>
									</CRow>
								</CAccordionBody>
							</CAccordionItem>
						</CAccordion>

						<CCardHeader><strong>Листи (Inbox)</strong></CCardHeader>
						<CCardBody>
							<p className="text-medium-emphasis small">
								<Link to={`${urlSupportLetters}/create`}>
									<CButton color="secondary" title="Створити новий запит вручну">
										<CIcon icon={cilPlus} customClassName="nav-icon" height={15} /> Створити
									</CButton>
								</Link>
							</p>
							<div style={{ height: 600, width: '100%' }}>
								<DataGrid
									rows={rows}
									columns={columns}
									page={page}
									pageSize={itemsPerPage}
									rowsPerPageOptions={[itemsPerPage]}
									pagination
									paginationMode="server"
									rowCount={rowCountState}
									loading={showLoader}
									onPageChange={handlePageChange}
									initialState={{
										sorting: { sortModel: [{ field: 'id', sort: 'desc' }] },
									}}
								/>
							</div>
							<div className="d-flex justify-content-start mt-3">
								<CButton color="secondary" variant="outline" onClick={handleBack} title="Повернутись до попередньої сторінки">
									Повернутись
								</CButton>
							</div>
						</CCardBody>
					</CCard>
				</CCol>
			</CRow>
		</>
	);
};

export default SupportLetters;
